import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, patchStrokes, patchRound, claimGroup, releaseGroup, getClaimMeta, takeoverClaim } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { ArrowLeft, Minus, Plus, CaretLeft, CaretDown, CaretUp, ChartBar, CloudSlash, CloudCheck, UsersThree, ArrowsClockwise, Lock, LockOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SyncStatusPill from '../components/SyncStatusPill';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';
import InstallPrompt from '../components/InstallPrompt';
import { setScoreEditUnlockToken, getGroupClaimData, setGroupClaimData, clearGroupClaim, getDeviceId } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import {
  playingHandicap, stablefordPoints, shotsOnHole,
  effectiveHolePars, effectiveStrokeIndexes,
  teeTimeForGroup, startHoleForGroup,
  playingOrderForGroup, playingPosForScorecardHole,
  isMultiStartRound, formatHandicapIndex, formatPlayingHandicap, getEffectivePH, getAllowedPH, displayWHS,
} from '../golf';
import { effectiveCourseForPlayer, effectiveHoleParsForPlayer, effectiveStrokeIndexesForPlayer } from '../lib/effectiveCourse';
import { countingPlayersForHole } from '../lib/teamContribution';
import { writeScoringHole, resumeHole, groupAggregateStrokes } from '../lib/scoringResume';
import { effectiveTeamScoring, roundFormat, formatValueLabel } from '../lib/formatHelpers';
import { sortedGroupIndexes, holeLabelForGroup, groupDisplayName } from '../lib/groupOrder';
import { setEnteringScores } from '../lib/updateGuard';
import ScrambleScoring from './ScrambleScoring';

function scoreColorClass(strokes, par) {
  if (!strokes || strokes <= 0) return '';
  const diff = strokes - par;
  if (diff <= -2) return 'score-eagle';
  if (diff === -1) return 'score-birdie';
  if (diff === 0) return 'score-par';
  if (diff === 1) return 'score-bogey';
  if (diff === 2) return 'score-double-bogey';
  return 'score-triple-plus';
}

// localStorage helpers
function getPendingKey(code, roundId) {
  return `foretour:pendingScores:${code}:${roundId}`;
}
function savePending(code, roundId, roundData) {
  try {
    localStorage.setItem(getPendingKey(code, roundId), JSON.stringify({
      hole_strokes: roundData.hole_strokes || {},
      hole_scores: roundData.hole_scores || {},
      ts: Date.now(),
    }));
  } catch {}
}
function loadPending(code, roundId) {
  try {
    const raw = localStorage.getItem(getPendingKey(code, roundId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function getGroupKey(code, roundId) {
  return `foretour:scoringGroup:${code}:${roundId}`;
}

// ── Editable player card (scorer's group) ──
function EditablePlayerCard({ player, strokes, ph, shots, stab, colorClass, teeName, counts, isSaving, isUnsaved, onMinus, onPlus, onDirectInput, fmt, holePar, defaultPar, playerSI, disabled, isHoleNr, onToggleNr }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    setInputVal(strokes > 0 ? String(strokes) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const commitEdit = () => {
    setEditing(false);
    const parsed = parseInt(inputVal, 10);
    if (isNaN(parsed) || parsed < 0 || parsed > 15) return; // invalid — revert
    if (parsed === strokes) return; // no change
    onDirectInput(player, parsed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.target.blur(); }
  };

  return (
    <Card className="bg-white border-[#E1E6E4] rounded-xl p-4" data-testid={`score-row-${player.id}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
          <div className="min-w-0 flex-1">
            <span className="font-semibold text-[#1D242B] text-sm truncate block" style={{ minWidth: 0 }}>{player.name}</span>
            <span className="text-[10px] text-[#455561] truncate block">
              {teeName ? `${teeName} · ` : ''}SI {playerSI}{holePar !== defaultPar ? ` · Par ${holePar}` : ''}
            </span>
          </div>
          <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-1.5 flex-shrink-0">PH {formatPlayingHandicap(ph)}</Badge>
          {shots > 0 && <Badge className="bg-[#E59000] text-white text-[10px] font-bold px-1.5 flex-shrink-0">+{shots}</Badge>}
          {counts && <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" data-testid={`counts-badge-${player.id}`}>COUNTS</Badge>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isUnsaved && <CloudSlash className="w-4 h-4 text-[#D32F2F]" data-testid={`unsaved-${player.id}`} />}
          {fmt !== 'nsp' && stab != null && stab >= 0 && (
            <span className={`text-sm font-bold ${stab >= 3 ? 'text-[#D32F2F]' : stab >= 2 ? 'text-[#1B4332]' : 'text-[#455561]'}`}>
              {stab} pts
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center gap-3">
        {isHoleNr ? (
          /* Hole-level NR (Stableford only) — tappable NR badge for undo */
          <div className="flex flex-col items-center gap-1.5">
            <button onClick={!disabled ? onToggleNr : undefined} disabled={disabled}
              className="w-20 h-20 rounded-2xl bg-[#FFF3E0] border-2 border-[#E59000]/30 flex items-center justify-center text-2xl font-bold text-[#E59000] active:bg-[#FFE0B2] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`nr-display-${player.id}`}>NR</button>
            {!disabled && (
              <span className="text-[10px] font-semibold text-[#E59000]" data-testid={`undo-hole-nr-${player.id}`}>Tap to undo</span>
            )}
          </div>
        ) : (
          <>
            <button onClick={onMinus} disabled={disabled}
              className="w-14 h-14 rounded-2xl bg-[#F4F6F5] flex items-center justify-center active:bg-[#E9EDEB] disabled:opacity-30 transition-colors flex-shrink-0"
              data-testid={`minus-${player.id}`}>
              <Minus className="w-6 h-6 text-[#1D242B]" weight="bold" />
            </button>
            {editing ? (
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value.replace(/\D/g, ''))}
                onBlur={commitEdit}
                onKeyDown={handleKeyDown}
                className={`w-20 h-20 rounded-2xl text-center text-3xl font-bold flex-shrink-0 outline-none ring-2 ring-[#1B4332] ${
                  strokes > 0 ? colorClass : 'bg-[#F4F6F5] text-[#1D242B]'
                }`}
                data-testid={`score-input-${player.id}`}
              />
            ) : (
              <button onClick={startEdit} disabled={disabled}
                className={`w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold transition-all flex-shrink-0 ${
                  strokes > 0 ? colorClass : 'bg-[#F4F6F5] text-[#B8C7C0] italic opacity-70'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid={`score-display-${player.id}`}>
                {strokes > 0 ? strokes : (holePar || '-')}
              </button>
            )}
            <button onClick={onPlus} disabled={disabled}
              className="w-14 h-14 rounded-2xl bg-[#F4F6F5] flex items-center justify-center active:bg-[#E9EDEB] disabled:opacity-30 transition-colors flex-shrink-0"
              data-testid={`plus-${player.id}`}>
              <Plus className="w-6 h-6 text-[#1D242B]" weight="bold" />
            </button>
            {/* Inline NR button — Stableford ONLY, same row as score controls */}
            {fmt !== 'nsp' && !disabled && (
              <button onClick={() => { if (editing) { setEditing(false); setInputVal(''); } onToggleNr(); }}
                className="w-11 h-11 rounded-xl bg-[#FFF3E0] border border-[#E59000]/30 text-[#E59000] text-xs font-bold flex items-center justify-center flex-shrink-0 active:bg-[#FFE0B2] transition-colors"
                data-testid={`mark-nr-${player.id}`}>
                NR
              </button>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ── Read-only group card (other groups in "All Groups" mode) ──
function ReadOnlyGroupCard({ groupIdx, groupName, teamPlayerIds, players, round, currentHole, par, si, expanded, onToggle, teamScoringActive, hideScores }) {
  const fmt = roundFormat(round);
  const isNSP = fmt === 'nsp';
  const teamPlayers = teamPlayerIds.map(id => players.find(p => p.id === id)).filter(Boolean);
  const countingSet = teamScoringActive && round ? countingPlayersForHole(round, teamPlayerIds, currentHole, par) : new Set();
  const holeData = teamPlayers.map(p => {
    const shouldHide = hideScores && (
      isMultiStartRound(round)
        ? playingPosForScorecardHole(round, groupIdx, currentHole) > (round?.reveal_hole || 0)
        : (currentHole + 1) > (round?.reveal_hole || 0)
    );
    const strokes = shouldHide ? 0 : ((round?.hole_strokes?.[p.id] || [])[currentHole] || 0);
    const playerCourse = round ? effectiveCourseForPlayer(round, p.id) : round?.course;
    const ph = playerCourse ? getAllowedPH(p, playerCourse, round) : 0;
    const playerPars = round ? effectiveHoleParsForPlayer(round, p.id) : [];
    const playerSIs = round ? effectiveStrokeIndexesForPlayer(round, p.id) : [];
    const playerPar = playerPars[currentHole] ?? par;
    const playerSI = playerSIs[currentHole] ?? si;
    const shots = shotsOnHole(ph, playerSI);
    const holeIsNr = (round?.hole_nr?.[p.id] || []).includes(currentHole);
    const stab = holeIsNr ? null : (strokes > 0 ? stablefordPoints(strokes, playerPar, ph, playerSI) : null);
    const teeName = round?.player_tees?.[p.id] ?? round?.course?.tee_name ?? null;
    return { player: p, strokes, ph, shots, stab, teeName, playerSI, playerPar };
  });
  const groupSfTotal = holeData.reduce((s, d) => s + Math.max(0, d.stab ?? 0), 0);
  const anyScored = holeData.some(d => d.strokes > 0);
  // Check if any player in this group has RAW scores for this hole (before hide masking)
  const anyRawScored = teamPlayers.some(p => ((round?.hole_strokes?.[p.id] || [])[currentHole] || 0) > 0);
  const isHiddenByFreeze = hideScores && anyRawScored && !anyScored;

  return (
    <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid={`readonly-group-${groupIdx}`}>
      <button onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
        data-testid={`toggle-group-${groupIdx}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="font-semibold text-[#1D242B] text-sm">{groupName}</span>
          <div className="flex flex-wrap gap-0.5">
            {teamPlayers.map(p => (
              <span key={p.id} className="text-[10px] text-[#455561] bg-[#F4F6F5] px-1.5 py-0.5 rounded">{p.name.split(' ')[0]}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {anyScored && !isNSP && (
            <Badge className="bg-[#E8E5DB] text-[#1B4332] text-xs font-bold">{groupSfTotal} pts</Badge>
          )}
          {expanded
            ? <CaretUp className="w-4 h-4 text-[#455561]" weight="bold" />
            : <CaretDown className="w-4 h-4 text-[#455561]" weight="bold" />
          }
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#E1E6E4] px-4 py-2 space-y-1.5 bg-[#FAFBFA]" data-testid={`group-detail-${groupIdx}`}>
          {!anyScored ? (
            isHiddenByFreeze ? (
              <p className="text-xs text-[#E59000] py-2 text-center font-semibold">&#x1F512; Scores hidden</p>
            ) : (
              <p className="text-xs text-[#B8C7C0] py-2 text-center">Not yet scored</p>
            )
          ) : (
            holeData.map(({ player, strokes, ph, stab, teeName, playerSI, playerPar }) => (
              <div key={player.id} className="flex items-center justify-between py-1" data-testid={`readonly-row-${player.id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-[#1D242B] truncate block" style={{ minWidth: 0 }}>{player.name}</span>
                    <span className="text-[9px] text-[#455561] truncate block">
                      {teeName ? `${teeName} · ` : ''}SI {playerSI}{playerPar !== par ? ` · Par ${playerPar}` : ''}
                    </span>
                  </div>
                  <span className="text-[10px] text-[#455561]">PH {formatPlayingHandicap(ph)}</span>
                  {countingSet.has(player.id) && <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`counts-badge-${player.id}`}>COUNTS</Badge>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-sm tabular-nums font-semibold ${strokes > 0 ? 'text-[#1D242B]' : 'text-[#B8C7C0]'}`}>
                    {strokes || '-'}
                  </span>
                  {!isNSP && (
                  <span className={`text-sm tabular-nums font-bold min-w-[32px] text-right ${
                    stab != null && stab >= 3 ? 'text-[#D32F2F]' : stab != null && stab >= 2 ? 'text-[#1B4332]' : 'text-[#455561]'
                  }`}>
                    {stab != null ? `${stab} pts` : '-'}
                  </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
}

// ── Main Scoring component ──
export default function Scoring() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();

  // Update guard: prevent auto-reload while entering scores
  useEffect(() => { setEnteringScores(true); return () => setEnteringScores(false); }, []);

  const tripCacheKey = `foretour:trip:${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(tripCacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [round, setRound] = useState(() => {
    if (!trip) return null;
    return trip.rounds?.find(r => r.id === roundId) || null;
  });
  const [loading, setLoading] = useState(!trip || !round);
  const [currentHole, setCurrentHole] = useState(0);
  const [resumeReady, setResumeReady] = useState(false);
  const userInteractedRef = useRef(false);
  const [view, setView] = useState('all');
  const [myGroupIdx, setMyGroupIdx] = useState(0);
  const [savingKey, setSavingKey] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [unsavedHoles, setUnsavedHoles] = useState(new Set());
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [groupSelected, setGroupSelected] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const patchInFlightRef = useRef(false);
  const pollRef = useRef(null);
  const etagRef = useRef(null);
  const [pendingOps, setPendingOps] = useState([]);
  // Track active player IDs for kick detection during polling
  const prevActiveIdsRef = useRef(null);
  // Track when a claim was just made — grace window for release-detection useEffect
  const claimMadeAtRef = useRef(0);

  // ── Session-expired guard: blocks ALL scoring until user re-authenticates ──
  const [sessionExpired, setSessionExpired] = useState(false);
  // ── Claim-moved guard: scoring moved to another phone ──
  const [claimMoved, setClaimMoved] = useState(false);
  // ── Takeover modal state ──
  const [takeoverTarget, setTakeoverTarget] = useState(null); // { group_idx, claimant_name, claimed_at, last_score_at }
  const [takeoverLoading, setTakeoverLoading] = useState(false);

  const isSessionError = useCallback((e) =>
    e?.response?.status === 403 && e?.response?.data?.code === 'invalid_trip_access', []);

  const handleSessionExpired = useCallback(() => {
    setSessionExpired(true);
    // Stop polling — saves are pointless until re-auth
    clearTimeout(pollRef.current);
  }, []);

  // Listen for the global trip-access-invalid event (fired by api.js interceptor
  // on ANY 403 invalid_trip_access, including polling/getTrip calls)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.code === code.toUpperCase()) handleSessionExpired();
    };
    window.addEventListener('trip-access-invalid', handler);
    return () => window.removeEventListener('trip-access-invalid', handler);
  }, [code, handleSessionExpired]);

  const access = getTripAccess(code);
  const isOrganiser = access?.role === 'organiser' || access?.legacy === true;

  // Flush pending operations when connectivity returns
  const flushPending = useCallback(async () => {
    if (patchInFlightRef.current || claimMoved) return;
    const ops = [...pendingOps];
    if (ops.length === 0) return;
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      patchInFlightRef.current = true;
      setSavingKey(`${op.playerId}-${op.hole}`);
      try {
        const res = await patchStrokes(code, roundId, {
          playerId: op.playerId, hole: op.hole, value: op.value,
          stableford: op.stableford, playerTotal: op.playerTotal,
        });
        // Minimal response — local optimistic state is authoritative; polling reconciles
        setLastSynced(new Date());
        setUnsavedHoles(prev => { const n = new Set(prev); n.delete(`${op.playerId}-${op.hole}`); return n; });
        setPendingOps(prev => prev.filter((_, j) => j !== 0));
      } catch (e) {
        if (e?.response?.status === 409 && e?.response?.data?.error === 'claim_moved') {
          // Claim moved: drop ALL remaining ops, flip to viewer
          setPendingOps([]);
          setUnsavedHoles(new Set());
          setClaimMoved(true);
          break;
        }
        if (isSessionError(e)) { handleSessionExpired(); break; }
        setPendingOps(prev => prev.map((p, j) => j === 0 ? { ...p, retries: (p.retries || 0) + 1 } : p));
        break;
      } finally { setSavingKey(null); patchInFlightRef.current = false; }
    }
  }, [pendingOps, code, roundId, isSessionError, handleSessionExpired, claimMoved]);

  useEffect(() => {
    const handler = () => { if (navigator.onLine) flushPending(); };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [flushPending]);

  // Part B: 30s retry interval for unsaved scores
  useEffect(() => {
    const id = setInterval(() => {
      if (pendingOps.length > 0 && !patchInFlightRef.current) flushPending();
    }, 30000);
    return () => clearInterval(id);
  }, [pendingOps.length, flushPending]);

  // Track groupSelected and myGroupIdx in refs for use in load() without stale closures
  const groupSelectedRef = useRef(groupSelected);
  const myGroupIdxRef = useRef(myGroupIdx);
  useEffect(() => { groupSelectedRef.current = groupSelected; }, [groupSelected]);
  useEffect(() => { myGroupIdxRef.current = myGroupIdx; }, [myGroupIdx]);

  const load = useCallback(async () => {
    if (patchInFlightRef.current) return;
    try {
      const res = await getTrip(code, etagRef.current);
      if (res.status === 304) return; // unchanged — keep local state
      etagRef.current = (res.headers?.etag || '').replace('W/', '') || null;
      const freshTrip = res.data;
      setTrip(freshTrip);
      try { sessionStorage.setItem(tripCacheKey, JSON.stringify(freshTrip)); } catch {}
      const r = freshTrip.rounds?.find(r => r.id === roundId);
      if (r) { setRound(r); savePending(code, roundId, r); }
      setLastSynced(new Date());

      // --- Claim-moved detection on poll: flip to view-only if another device took over ---
      if (r && groupSelectedRef.current && myGroupIdxRef.current >= 0) {
        const activeClaim = (r.group_claims || []).find(
          c => c.group_idx === myGroupIdxRef.current && new Date(c.expires_at) > new Date()
        );
        if (activeClaim && activeClaim.device_id && activeClaim.device_id !== getDeviceId()) {
          setClaimMoved(true);
        }
      }

      // --- Kick detection: check if any active player became inactive ---
      const allPlayers = freshTrip.players || [];
      const activeIds = new Set(allPlayers.filter(p => p.active !== false).map(p => p.id));
      if (prevActiveIdsRef.current !== null) {
        const prev = prevActiveIdsRef.current;
        const deactivated = [...prev].filter(id => !activeIds.has(id));
        if (deactivated.length > 0) {
          toast.warning('Your access to this trip has been paused by the organiser.', { duration: 5000 });
        }
      }
      prevActiveIdsRef.current = activeIds;
    } catch (e) {
      if (isSessionError(e)) handleSessionExpired();
    } finally { setLoading(false); }
  }, [code, roundId, tripCacheKey, isSessionError, handleSessionExpired]);

  // Claim-moved detection on focus/visibilitychange: re-fetch trip on tab return
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' || document.hasFocus?.()) {
        load(); // poll immediately — load() checks claim device_id
      }
    };
    document.addEventListener('visibilitychange', handler);
    window.addEventListener('focus', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('focus', handler);
    };
  }, [load]);

  useEffect(() => {
    const pending = loadPending(code, roundId);
    if (pending) {
      setRound(prev => prev ? { ...prev, hole_strokes: pending.hole_strokes, hole_scores: pending.hole_scores } : prev);
    }
    try {
      const savedIdx = localStorage.getItem(getGroupKey(code, roundId));
      if (savedIdx !== null) {
        setMyGroupIdx(parseInt(savedIdx, 10));
        setGroupSelected(true);
        setView('my-group');
      }
    } catch {}
    load();
  }, [code, roundId, load]);

  useEffect(() => {
    if (!loading && round && round.teams?.length > 0 && !groupSelected) {
      if (localStorage.getItem(getGroupKey(code, roundId)) === null) setShowGroupPicker(true);
    }
  }, [loading, round, groupSelected, code, roundId]);

  // Set starting hole when round loads with a pre-selected group (restored from localStorage)
  const startHoleInitRef = useRef(false);
  useEffect(() => {
    if (round && groupSelected && !startHoleInitRef.current) {
      startHoleInitRef.current = true;
      const strokes = groupAggregateStrokes(round, myGroupIdx);
      const order = playingOrderForGroup(round, myGroupIdx);
      const h = resumeHole(code, roundId, String(myGroupIdx), strokes, order);
      if (!userInteractedRef.current) setCurrentHole(h);
      setResumeReady(true);
    }
  }, [round, groupSelected, myGroupIdx, code, roundId]);

  // Persist current hole to sessionStorage on every change
  useEffect(() => {
    if (groupSelected && round) {
      writeScoringHole(code, roundId, String(myGroupIdx), currentHole);
    }
  }, [currentHole, code, roundId, myGroupIdx, groupSelected, round]);

  useEffect(() => {
    // Scoring poll: jitter 8s ±2s, visibility-gated
    const tick = () => {
      const jitter = 6000 + Math.random() * 4000;
      pollRef.current = setTimeout(() => {
        if (!patchInFlightRef.current) load();
        tick();
      }, jitter);
    };
    const stop = () => clearTimeout(pollRef.current);
    const onVis = () => {
      if (document.hidden) {
        stop();
      } else {
        if (!patchInFlightRef.current) load();
        tick();
      }
    };
    tick();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', stop);
    window.addEventListener('pageshow', () => { if (!document.hidden) { if (!patchInFlightRef.current) load(); tick(); } });
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', stop);
    };
  }, [load]);

  // ── Auth heartbeat (~60s, visibility-gated) ──
  // Catches session loss while user is idle on the Scoring screen.
  // Uses GET /trips/{code} with current ETag — 304 when unchanged (cheap),
  // 403 when auth invalid → routed through handleSessionExpired via the
  // trip-access-invalid event listener already wired above.
  useEffect(() => {
    if (sessionExpired) return; // no point pinging once loss is surfaced

    let heartbeatTimer = null;

    const ping = async () => {
      if (sessionExpired || patchInFlightRef.current) return;
      try {
        await getTrip(code, etagRef.current);
      } catch {
        // 403 already handled by api.js interceptor → trip-access-invalid event
      }
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      heartbeatTimer = setInterval(ping, 60_000);
    };

    const stopHeartbeat = () => {
      if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopHeartbeat();
      } else {
        // Immediate check on foregrounding (catches "phone locked overnight" case)
        ping();
        startHeartbeat();
      }
    };

    // Start immediately if tab is visible
    if (!document.hidden) startHeartbeat();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopHeartbeat();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [code, sessionExpired]);

  // Kick to group picker if ALL players in the selected group become inactive
  useEffect(() => {
    if (!trip || !round || !groupSelected) return;
    const teamIds = round.teams?.[myGroupIdx] || [];
    if (teamIds.length === 0) return;
    const allPlayers = trip.players || [];
    const activeInGroup = teamIds.filter(id => {
      const p = allPlayers.find(pl => pl.id === id);
      return p && p.active !== false;
    });
    if (activeInGroup.length === 0) {
      setGroupSelected(false);
      setShowGroupPicker(true);
      setView('all');
      localStorage.removeItem(getGroupKey(code, roundId));
    }
  }, [trip, round, myGroupIdx, groupSelected, code, roundId]);

  // Detect released claim: if we have a stored claim but it's no longer in round.group_claims,
  // redirect back to group picker. Fires on mount + whenever round data refreshes (polling/ETag).
  // Grace window: skip for 15s after a fresh claim to let polling sync.
  useEffect(() => {
    if (!round || !groupSelected) return;
    if (Date.now() - claimMadeAtRef.current < 15000) return; // freshly claimed — wait for sync
    const myClaimData = getGroupClaimData(code, roundId);
    if (!myClaimData?.token) return; // no stored claim — nothing to validate
    const serverClaims = round.group_claims || [];
    if (serverClaims.length === 0) return; // no claims at all — can't infer release from empty state
    const stillExists = serverClaims.some(c => c.group_idx === myClaimData.group_idx);
    if (!stillExists) {
      clearGroupClaim(code, roundId);
      setGroupSelected(false);
      setShowGroupPicker(true);
      toast.info('Your group was released by the organiser — please choose your group again.');
    }
  }, [round?.group_claims, groupSelected, code, roundId]); // eslint-disable-line react-hooks/exhaustive-deps


  const players = trip?.players || [];
  const teams = round?.teams || [];
  const holePars = round ? effectiveHolePars(round) : Array(18).fill(4);
  const strokeIndexes = round ? effectiveStrokeIndexes(round) : Array.from({length:18}, (_,i) => i+1);
  const par = holePars[currentHole] || 4;
  const si = strokeIndexes[currentHole] || (currentHole + 1);
  // Other groups' scores hidden when freeze is active and not yet revealed
  const hideOtherScores = round?.freeze_enabled !== false && (round?.reveal_hole > 0 && !round?.revealed);

  // Revealed-round / group-finished score lock: editing locked unless owner-unlocked
  const isRevealed = round?.revealed === true;
  const isGroupFinished = Boolean(round?.group_finished?.[myGroupIdx]);
  // Always start locked for revealed/finished rounds — never auto-trust stale
  // sessionStorage tokens. User must re-confirm owner password each visit.
  const [scoreEditUnlocked, setScoreEditUnlocked] = useState(false);
  const [scoreUnlockDialogOpen, setScoreUnlockDialogOpen] = useState(false);
  const [confirmGroupIdx, setConfirmGroupIdx] = useState(null);
  const [finishNrDialog, setFinishNrDialog] = useState(null); // { nrPlayers: [{id, name, missingHoles}] } or null
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  const [pendingNrPlayerIds, setPendingNrPlayerIds] = useState([]);
  // Stage 1: sequential soft-prompt queue for missing holes
  const [softPromptQueue, setSoftPromptQueue] = useState([]); // [holeIdx, ...]  (distinct holes)
  const [softPromptIsFinish, setSoftPromptIsFinish] = useState(false); // mid-round vs Finish Round
  const [skippedFinishHoles, setSkippedFinishHoles] = useState(new Set()); // holes skipped at Finish Round (for NR)
  const [dismissedMissingHoles, setDismissedMissingHoles] = useState(new Set()); // mid-round "Remind me later"
  const [pendingNavTarget, setPendingNavTarget] = useState(null); // where to go after mid-round dismissals
  const [allParConfirmOpen, setAllParConfirmOpen] = useState(false); // "No scores entered — confirm all Par?"
  const softPromptItem = softPromptQueue.length > 0 ? softPromptQueue[0] : null;
  const scoreLocked = ((isRevealed || isGroupFinished) && !scoreEditUnlocked) || claimMoved;

  const myGroupPlayers = teams.length > 0
    ? (teams[myGroupIdx] || []).map(id => players.find(p => p.id === id)).filter(p => p && p.active !== false)
    : players.filter(p => p.active !== false);

  const getStrokes = (playerId) => (round?.hole_strokes?.[playerId] || [])[currentHole] || 0;
  const getPH = (player) => round ? getAllowedPH(player, effectiveCourseForPlayer(round, player.id), round) : 0;
  const getPlayerPar = (playerId) => round ? effectiveHoleParsForPlayer(round, playerId)[currentHole] ?? par : par;
  const getPlayerSI = (playerId) => round ? effectiveStrokeIndexesForPlayer(round, playerId)[currentHole] ?? si : si;
  const getTeeName = (playerId) => round?.player_tees?.[playerId] ?? round?.course?.tee_name ?? null;

  const selectGroup = async (idx) => {
    // If round has teams and claims are in play, attempt to claim
    if (teams.length > 0) {
      const claimantName = isOrganiser ? 'Organiser' : 'Scorer';
      const claims = round?.group_claims || [];
      const existing = claims.find(c => c.group_idx === idx && new Date(c.expires_at) > new Date());
      const myClaimData = getGroupClaimData(code, roundId);
      const alreadyMine = myClaimData?.group_idx === idx && myClaimData?.token;
      const deviceId = getDeviceId();

      if (!alreadyMine) {
        // Check if another device has an active claim — show takeover modal
        if (existing) {
          const myDeviceId = deviceId;
          const claimDeviceId = existing.device_id || '';
          if (claimDeviceId && claimDeviceId !== myDeviceId) {
            // Show takeover modal instead of blocking
            setTakeoverTarget({
              group_idx: idx,
              claimant_name: existing.claimant_name || 'Someone',
              claimed_at: existing.claimed_at || '',
              last_score_at: existing.last_score_at || null,
              by_organiser: existing.by_organiser || false,
            });
            return;
          }
        }
        try {
          const res = await claimGroup(code, roundId, { group_idx: idx, claimant_name: claimantName, device_id: deviceId });
          setGroupClaimData(code, roundId, { token: res.data.token, group_idx: idx, expires_at: res.data.expires_at });
          claimMadeAtRef.current = Date.now();
        } catch (e) {
          if (e.response?.status === 409) {
            // Claim already exists — check if we should offer takeover
            const claimedBy = e.response?.data?.claimed_by || 'someone';
            const claimedAt = e.response?.data?.claimed_at || '';
            // Try fetching claim meta for takeover info
            try {
              const meta = await getClaimMeta(code, roundId, idx);
              if (meta.data?.claimed && meta.data.device_id !== deviceId) {
                setTakeoverTarget({
                  group_idx: idx,
                  claimant_name: meta.data.claimant_name || claimedBy,
                  claimed_at: meta.data.claimed_at || claimedAt,
                  last_score_at: meta.data.last_score_at || null,
                });
                return;
              }
            } catch {}
            toast.error(`Group already claimed by ${claimedBy}. Pick another or ask them to release.`);
          } else if (e.response?.data?.code === 'invalid_trip_access' || e.response?.data?.detail?.includes?.('trip access')) {
            clearGroupClaim(code, roundId);
            handleSessionExpired();
          } else {
            toast.error('Unable to claim group. Check your connection and try again.');
          }
          load(); // refresh round data
          return; // abort — do NOT proceed without a valid claim
        }
      }
    }
    setClaimMoved(false);
    // Clear stale pending data so fresh claim starts from server state
    try { localStorage.removeItem(getPendingKey(code, roundId)); } catch {}
    setPendingOps([]);
    setUnsavedHoles(new Set());
    setMyGroupIdx(idx);
    setGroupSelected(true);
    setView('my-group');
    setShowGroupPicker(false);
    localStorage.setItem(getGroupKey(code, roundId), String(idx));
    if (round) {
      userInteractedRef.current = true;
      const strokes = groupAggregateStrokes(round, idx);
      const order = playingOrderForGroup(round, idx);
      const h = resumeHole(code, roundId, String(idx), strokes, order);
      setCurrentHole(h);
      setResumeReady(true);
    }
  };

  const handleTakeover = async () => {
    if (!takeoverTarget) return;
    setTakeoverLoading(true);
    try {
      const deviceId = getDeviceId();
      const res = await takeoverClaim(code, roundId, takeoverTarget.group_idx, {
        device_id: deviceId,
        claimant_name: isOrganiser ? 'Organiser' : 'Scorer',
      });
      setGroupClaimData(code, roundId, {
        token: res.data.token,
        group_idx: takeoverTarget.group_idx,
        expires_at: res.data.expires_at,
      });
      claimMadeAtRef.current = Date.now();
      setClaimMoved(false);
      // Clear stale pending data so take-back starts fresh from server
      try { localStorage.removeItem(getPendingKey(code, roundId)); } catch {}
      setPendingOps([]);
      setUnsavedHoles(new Set());
      setMyGroupIdx(takeoverTarget.group_idx);
      setGroupSelected(true);
      setView('my-group');
      setShowGroupPicker(false);
      setTakeoverTarget(null);
      localStorage.setItem(getGroupKey(code, roundId), String(takeoverTarget.group_idx));
      await load(); // refresh scores — replaces all local state from server
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response?.data?.detail === 'group_finished' ? 'Group is already finished.' : 'Cannot take over — round revealed.');
      } else {
        toast.error('Takeover failed. Try again.');
      }
    } finally {
      setTakeoverLoading(false);
    }
  };

  const handleViewChange = (v) => {
    if (v === 'my-group' && !groupSelected && teams.length > 0) { setShowGroupPicker(true); return; }
    setView(v);
  };

  const toggleGroupExpand = (idx) => {
    setExpandedGroups(prev => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  // ── Shared handler for 409 claim_moved responses ──
  // Reverts optimistic state for the given player/hole, drops the op, and flips to viewer mode.
  const handleClaimMovedError = useCallback((playerId, hole, prevValue, prevStab) => {
    // Revert optimistic local change to last-known server value
    if (playerId != null && hole != null) {
      setRound(prev => {
        if (!prev) return prev;
        const hs = { ...prev.hole_strokes }; const hsc = { ...prev.hole_scores };
        hs[playerId] = [...(hs[playerId] || Array(18).fill(0))];
        hsc[playerId] = [...(hsc[playerId] || Array(18).fill(0))];
        hs[playerId][hole] = prevValue ?? 0;
        hsc[playerId][hole] = prevStab ?? 0;
        return { ...prev, hole_strokes: hs, hole_scores: hsc };
      });
    }
    // Drop ALL pending ops — never retry after claim lost
    setPendingOps([]);
    setUnsavedHoles(new Set());
    // Clear localStorage pending cache so stale data doesn't resurface
    try { localStorage.removeItem(getPendingKey(code, roundId)); } catch {}
    setClaimMoved(true);
  }, [code, roundId]);

  const isClaimMovedError = (e) =>
    e?.response?.status === 409 && e?.response?.data?.error === 'claim_moved';

  const handleStroke = async (player, delta, fromAutoFill = false) => {
    if (typeof navigator.vibrate === 'function') navigator.vibrate([10]);
    const current = getStrokes(player.id);
    // When unscored (0) and user manually presses +/-, first tap sets par.
    // Auto-fill from handleNext passes fromAutoFill=true to keep original behavior.
    if (current === 0 && !fromAutoFill) {
      // First tap on unscored hole → set to par (both + and −)
      const newVal = par;
      const ph = getPH(player);
      const playerPar = getPlayerPar(player.id);
      const playerSI = getPlayerSI(player.id);
      const stab = stablefordPoints(newVal, playerPar, ph, playerSI);
      const allHoleScores = round?.hole_scores?.[player.id] || [];
      const playerNrHoles = round?.hole_nr?.[player.id] || [];
      const totalSoFar = allHoleScores.reduce((s, v, i) => {
        if (i === currentHole) return s + Math.max(0, stab ?? 0);
        if (playerNrHoles.includes(i)) return s; // NR holes = 0 pts
        return s + Math.max(0, v ?? 0);
      }, 0);

      setRound(prev => {
        if (!prev) return prev;
        const hs = { ...prev.hole_strokes }; const hsc = { ...prev.hole_scores };
        hs[player.id] = [...(hs[player.id] || Array(18).fill(0))];
        hsc[player.id] = [...(hsc[player.id] || Array(18).fill(0))];
        hs[player.id][currentHole] = newVal;
        hsc[player.id][currentHole] = Math.max(0, stab ?? 0);
        const updated = { ...prev, hole_strokes: hs, hole_scores: hsc };
        savePending(code, roundId, updated);
        return updated;
      });

      const key = `${player.id}-${currentHole}`;
      setSavingKey(key);
      patchInFlightRef.current = true;
      try {
        await patchStrokes(code, roundId, {
          playerId: player.id, hole: currentHole, value: newVal,
          stableford: Math.max(0, stab ?? 0), playerTotal: totalSoFar,
        });
        setLastSynced(new Date());
        setUnsavedHoles(prev => { const n = new Set(prev); n.delete(key); return n; });
      } catch (e) {
        if (isClaimMovedError(e)) {
          handleClaimMovedError(player.id, currentHole, current, 0);
        } else if (e?.response?.status === 403 && e?.response?.data?.detail === 'player_not_in_claimed_group') {
          setUnsavedHoles(prev => new Set(prev).add(key));
          setPendingOps(prev => [...prev, {
            playerId: player.id, playerName: player.name, hole: currentHole,
            value: newVal, stableford: Math.max(0, stab ?? 0), playerTotal: totalSoFar,
            ts: Date.now(), retries: 0,
          }]);
        }
      } finally { setSavingKey(null); patchInFlightRef.current = false; }
      return;
    }
    const base = (current > 0 || fromAutoFill) ? current : par;
    const newVal = Math.max(fromAutoFill ? 0 : 1, base + delta);
    const ph = getPH(player);
    const playerPar = getPlayerPar(player.id);
    const playerSI = getPlayerSI(player.id);
    const stab = newVal > 0 ? stablefordPoints(newVal, playerPar, ph, playerSI) : null;
    const allHoleScores = round?.hole_scores?.[player.id] || [];
    const playerNrHoles = round?.hole_nr?.[player.id] || [];
    const totalSoFar = allHoleScores.reduce((s, v, i) => {
      if (i === currentHole) return s + Math.max(0, stab ?? 0);
      if (playerNrHoles.includes(i)) return s; // NR holes = 0 pts
      return s + Math.max(0, v ?? 0);
    }, 0);

    setRound(prev => {
      if (!prev) return prev;
      const hs = { ...prev.hole_strokes }; const hsc = { ...prev.hole_scores };
      hs[player.id] = [...(hs[player.id] || Array(18).fill(0))];
      hsc[player.id] = [...(hsc[player.id] || Array(18).fill(0))];
      hs[player.id][currentHole] = newVal;
      hsc[player.id][currentHole] = Math.max(0, stab ?? 0);
      const updated = { ...prev, hole_strokes: hs, hole_scores: hsc };
      savePending(code, roundId, updated);
      return updated;
    });

    const key = `${player.id}-${currentHole}`;
    setSavingKey(key);
    patchInFlightRef.current = true;
    try {
      const res = await patchStrokes(code, roundId, {
        playerId: player.id, hole: currentHole, value: newVal,
        stableford: Math.max(0, stab ?? 0), playerTotal: totalSoFar,
      });
      // Minimal response — optimistic local update (above) is authoritative; polling reconciles
      setLastSynced(new Date());
      setUnsavedHoles(prev => { const n = new Set(prev); n.delete(key); return n; });
    } catch (e) {
      if (isClaimMovedError(e)) {
        const prevStab = round?.hole_scores?.[player.id]?.[currentHole] ?? 0;
        handleClaimMovedError(player.id, currentHole, current, prevStab);
      } else if (e?.response?.status === 403 && e?.response?.data?.detail === 'player_not_in_claimed_group') {
        clearGroupClaim(code, roundId);
        setGroupSelected(false);
        setShowGroupPicker(true);
        toast.info('Your group was released — please choose your group again.');
      } else if (isSessionError(e)) {
        // Roll back optimistic state for this hole
        setRound(prev => {
          if (!prev) return prev;
          const hs = { ...prev.hole_strokes }; const hsc = { ...prev.hole_scores };
          hs[player.id] = [...(hs[player.id] || Array(18).fill(0))];
          hsc[player.id] = [...(hsc[player.id] || Array(18).fill(0))];
          hs[player.id][currentHole] = current; // restore previous value
          hsc[player.id][currentHole] = round?.hole_scores?.[player.id]?.[currentHole] ?? 0;
          return { ...prev, hole_strokes: hs, hole_scores: hsc };
        });
        handleSessionExpired();
      } else {
        setUnsavedHoles(prev => new Set(prev).add(key));
        setPendingOps(prev => [...prev, {
          playerId: player.id, playerName: player.name, hole: currentHole,
          value: newVal, stableford: Math.max(0, stab ?? 0), playerTotal: totalSoFar,
          ts: Date.now(), retries: 0,
        }]);
      }
    } finally { setSavingKey(null); patchInFlightRef.current = false; }
  };

  // Playing order for the current group (wrapping 18-hole sequence)
  const playingOrder = round && groupSelected ? playingOrderForGroup(round, myGroupIdx) : Array.from({length:18}, (_,i) => i);
  const currentPosInOrder = playingOrder.indexOf(currentHole);

  // --- Missing-hole detection (grouped by HOLE, not player-hole) ---
  const getDistinctMissingHoles = (upToPos, roundData) => {
    // Returns distinct hole indices in playing-order positions 0..upToPos-1
    // where ANY player lacks a score (excluding hole_nr holes).
    // roundData: pass explicit round object to avoid stale-closure reads.
    const r = roundData || round;
    const missing = [];
    for (let pos = 0; pos < upToPos; pos++) {
      const h = playingOrder[pos];
      const anyMissing = myGroupPlayers.some(p => {
        const playerNr = r?.hole_nr?.[p.id] || [];
        if (playerNr.includes(h)) return false;
        const s = r?.hole_strokes?.[p.id]?.[h];
        return !s || s <= 0;
      });
      if (anyMissing) missing.push(h);
    }
    return missing;
  };

  // Wrap navigation with missing-hole check (mid-round)
  // roundData: optional fresh round to avoid stale closures after auto-fill.
  const navigateWithCheck = (targetHoleIdx, roundData) => {
    userInteractedRef.current = true;
    if (scoreLocked) { setCurrentHole(targetHoleIdx); return; }
    const targetPos = playingOrder.indexOf(targetHoleIdx);
    const currentPos = playingOrder.indexOf(currentHole);
    // Only check when moving FORWARD (skipping past holes)
    if (targetPos <= currentPos) { setCurrentHole(targetHoleIdx); return; }
    const allMissing = getDistinctMissingHoles(targetPos, roundData);
    const promptable = allMissing.filter(h => !dismissedMissingHoles.has(h));
    if (promptable.length > 0) {
      setSoftPromptQueue(promptable);
      setSoftPromptIsFinish(false);
      setPendingNavTarget(targetHoleIdx);
      return;
    }
    setCurrentHole(targetHoleIdx);
  };

  const handleNext = async () => {
    // Auto-fill uncommitted players on current hole, capturing the fresh round state.
    let freshRound = round;
    if (!scoreLocked) {
      const uncommitted = myGroupPlayers.filter(p => {
        if (getStrokes(p.id) !== 0) return false;
        // Never auto-fill a hole that is already NR
        if ((round?.hole_nr?.[p.id] || []).includes(currentHole)) return false;
        return true;
      });
      if (uncommitted.length > 0) {
        // Check: is the ENTIRE group uncommitted (no one has an explicit score or NR)?
        const hasAnyExplicit = myGroupPlayers.some(p => {
          const s = (round?.hole_strokes?.[p.id] || [])[currentHole] || 0;
          const isNr = (round?.hole_nr?.[p.id] || []).includes(currentHole);
          return s > 0 || isNr;
        });
        if (!hasAnyExplicit) {
          // Whole hole about to be Par-defaulted — ask for confirmation
          setAllParConfirmOpen(true);
          return;
        }
        // Compute the round state AFTER auto-fill (same logic as handleStroke's setRound updater)
        let updated = { ...round };
        const hs = { ...updated.hole_strokes };
        const hsc = { ...updated.hole_scores };
        for (const p of uncommitted) {
          const ph = getPH(p);
          const playerPar = getPlayerPar(p.id);
          const playerSI = getPlayerSI(p.id);
          const stab = stablefordPoints(par, playerPar, ph, playerSI);
          hs[p.id] = [...(hs[p.id] || Array(18).fill(0))];
          hsc[p.id] = [...(hsc[p.id] || Array(18).fill(0))];
          hs[p.id][currentHole] = par;
          hsc[p.id][currentHole] = Math.max(0, stab ?? 0);
        }
        updated = { ...updated, hole_strokes: hs, hole_scores: hsc };
        freshRound = updated;
        // Fire the actual async auto-fill (writes to React state + server)
        await Promise.all(uncommitted.map(p => handleStroke(p, par, true)));
      }
    }
    if (currentPosInOrder < 17) {
      const nextHole = playingOrder[currentPosInOrder + 1];
      navigateWithCheck(nextHole, freshRound);
    } else {
      if (!isGroupFinished) {
        // Navigate to Review Scorecard — handles missing holes, NR, and lock
        navigate(`/trip/${code}/round/${roundId}/review/${myGroupIdx}`);
        return;
      }
      navigate(`/trip/${code}/round/${roundId}/setup`);
    }
  };

  const handlePrev = () => {
    userInteractedRef.current = true;
    if (currentPosInOrder > 0) setCurrentHole(playingOrder[currentPosInOrder - 1]);
  };

  // Stage 1 soft-prompt handlers

  // "All Par" confirmation: user confirms, we proceed with auto-fill + Next
  const handleAllParConfirm = async () => {
    setAllParConfirmOpen(false);
    // Re-run the full handleNext flow — this time hasAnyExplicit check won't fire
    // because we'll bypass it by auto-filling first then navigating.
    let freshRound = round;
    const uncommitted = myGroupPlayers.filter(p => getStrokes(p.id) === 0);
    if (uncommitted.length > 0) {
      let updated = { ...round };
      const hs = { ...updated.hole_strokes };
      const hsc = { ...updated.hole_scores };
      for (const p of uncommitted) {
        const ph = getPH(p);
        const playerPar = getPlayerPar(p.id);
        const playerSI = getPlayerSI(p.id);
        const stab = stablefordPoints(par, playerPar, ph, playerSI);
        hs[p.id] = [...(hs[p.id] || Array(18).fill(0))];
        hsc[p.id] = [...(hsc[p.id] || Array(18).fill(0))];
        hs[p.id][currentHole] = par;
        hsc[p.id][currentHole] = Math.max(0, stab ?? 0);
      }
      updated = { ...updated, hole_strokes: hs, hole_scores: hsc };
      freshRound = updated;
      await Promise.all(uncommitted.map(p => handleStroke(p, par, true)));
    }
    if (currentPosInOrder < 17) {
      navigateWithCheck(playingOrder[currentPosInOrder + 1], freshRound);
    } else {
      // Navigate to Review Scorecard
      navigate(`/trip/${code}/round/${roundId}/review/${myGroupIdx}`);
    }
  };

  const handleSoftPromptGoTo = () => {
    if (!softPromptItem) return;
    setCurrentHole(softPromptItem);
    setSoftPromptQueue([]);
    setPendingNavTarget(null);
    setSkippedFinishHoles(new Set());
  };

  const handleSoftPromptSecondary = () => {
    if (softPromptItem == null) return;
    const hole = softPromptItem;
    const remaining = softPromptQueue.slice(1);

    if (softPromptIsFinish) {
      // Finish Round: "Skip — mark as NR"
      const nextSkipped = new Set(skippedFinishHoles);
      nextSkipped.add(hole);
      if (remaining.length > 0) {
        setSkippedFinishHoles(nextSkipped);
        setSoftPromptQueue(remaining);
      } else {
        // All resolved — fire Stage 2 NR confirmation with player-level detail
        setSoftPromptQueue([]);
        setSkippedFinishHoles(new Set());
        const nrPlayers = [];
        for (const p of myGroupPlayers) {
          const playerNr = round?.hole_nr?.[p.id] || [];
          const missingHoles = [];
          for (const h of nextSkipped) {
            if (playerNr.includes(h)) continue;
            const s = round?.hole_strokes?.[p.id]?.[h];
            if (!s || s <= 0) missingHoles.push(h + 1);
          }
          if (missingHoles.length > 0) nrPlayers.push({ id: p.id, name: p.name, missingHoles });
        }
        if (nrPlayers.length > 0) {
          setFinishNrDialog({ nrPlayers });
          setPendingNrPlayerIds(nrPlayers.map(p => p.id));
        } else {
          setPendingNrPlayerIds([]);
          setFinishConfirmOpen(true);
        }
      }
    } else {
      // Mid-round: "Remind me later" — no NR, just dismiss
      setDismissedMissingHoles(prev => new Set(prev).add(hole));
      if (remaining.length > 0) {
        setSoftPromptQueue(remaining);
      } else {
        // All dismissed — proceed to intended destination
        setSoftPromptQueue([]);
        if (pendingNavTarget != null) {
          setCurrentHole(pendingNavTarget);
          setPendingNavTarget(null);
        }
      }
    }
  };


  const handleDirectInput = async (player, absoluteValue) => {
    if (typeof navigator.vibrate === 'function') navigator.vibrate([10]);
    const current = getStrokes(player.id);
    const delta = absoluteValue - current;
    if (delta === 0) return;
    await handleStroke(player, delta, true);
  };

  const doFinishRound = async (nrIds = []) => {
    try {
      const gf = [...(round.group_finished || Array(round.teams?.length || 0).fill(false))];
      while (gf.length <= myGroupIdx) gf.push(false);
      gf[myGroupIdx] = true;
      const existingNr = round.nr_players || [];
      const mergedNr = [...new Set([...existingNr, ...nrIds])];
      await patchRound(code, roundId, { group_finished: gf, nr_players: mergedNr });
    } catch (e) {
      if (isSessionError(e)) { handleSessionExpired(); return; }
      /* best-effort for other errors */
    }
    navigate(`/trip/${code}/round/${roundId}/setup`);
  };

  const handleUndoNr = async (playerId) => {
    const existingNr = round?.nr_players || [];
    const updated = existingNr.filter(id => id !== playerId);
    try {
      await patchRound(code, roundId, { nr_players: updated });
      setRound(prev => prev ? { ...prev, nr_players: updated } : prev);
      toast.success('NR status removed');
    } catch (e) {
      if (isSessionError(e)) handleSessionExpired();
      else toast.error('Failed to undo NR');
    }
  };

  // ── Hole-level NR (Stableford ONLY) ──
  // Deliberate format divergence: NSP requires a gross score for every hole;
  // Stableford allows pick-up (0 points). Do NOT extend to NSP.
  const isHoleNr = (playerId, hole) => (round?.hole_nr?.[playerId] || []).includes(hole);

  const handleToggleHoleNr = async (player) => {
    const pid = player.id;
    const holeNr = { ...(round?.hole_nr || {}) };
    const playerNr = [...(holeNr[pid] || [])];
    const isCurrentlyNr = playerNr.includes(currentHole);

    if (isCurrentlyNr) {
      // Remove NR — restore hole to uninitialised (empty for fresh entry)
      holeNr[pid] = playerNr.filter(h => h !== currentHole);
      if (holeNr[pid].length === 0) delete holeNr[pid];
      setRound(prev => {
        if (!prev) return prev;
        const hs = { ...prev.hole_strokes };
        const hsc = { ...prev.hole_scores };
        const sArr = [...(hs[pid] || Array(18).fill(0))];
        const pArr = [...(hsc[pid] || Array(18).fill(-1))];
        sArr[currentHole] = 0;    // uninitialised sentinel for strokes
        pArr[currentHole] = -1;   // uninitialised sentinel for hole_scores
        hs[pid] = sArr;
        hsc[pid] = pArr;
        return { ...prev, hole_nr: { ...holeNr }, hole_strokes: hs, hole_scores: hsc };
      });
    } else {
      // Set NR — 0 points, 0 strokes for this hole
      holeNr[pid] = [...playerNr, currentHole];
      setRound(prev => {
        if (!prev) return prev;
        const hs = { ...prev.hole_strokes };
        const hsc = { ...prev.hole_scores };
        const sArr = [...(hs[pid] || Array(18).fill(0))];
        const pArr = [...(hsc[pid] || Array(18).fill(-1))];
        sArr[currentHole] = 0;
        pArr[currentHole] = 0;
        hs[pid] = sArr;
        hsc[pid] = pArr;
        return { ...prev, hole_nr: { ...holeNr }, hole_strokes: hs, hole_scores: hsc };
      });
    }
    // Persist: send hole_nr via patchRound, and zero/reset the specific hole via patchStrokes
    try {
      // 1. Update hole_nr flag on the round
      await patchRound(code, roundId, { hole_nr: holeNr });
      // 2. Update the specific hole's strokes + scores on the server via patchStrokes
      //    (uses dot-notation so only this player's hole is touched, safe for concurrency)
      const nrPlayerNrHoles = holeNr[pid] || [];
      // Compute the correct playerTotal by iterating hole_scores with NR filter
      const allHS = round?.hole_scores?.[pid] || [];
      const newTotal = allHS.reduce((s, v, i) => {
        if (i === currentHole) return s; // this hole contributes 0 (NR on or empty after undo)
        if (nrPlayerNrHoles.includes(i)) return s; // other NR holes = 0
        return s + Math.max(0, v ?? 0);
      }, 0);
      await patchStrokes(code, roundId, {
        playerId: pid,
        hole: currentHole,
        value: 0,
        stableford: isCurrentlyNr ? -1 : 0, // undo → uninitialised sentinel; set → 0 pts
        playerTotal: newTotal,
      });
    } catch (e) {
      if (isSessionError(e)) handleSessionExpired();
      else if (e?.response?.status === 403 && e?.response?.data?.detail === 'player_not_in_claimed_group') {
        clearGroupClaim(code, roundId);
        setGroupSelected(false);
        setShowGroupPicker(true);
        toast.error('Group claim expired or reassigned. Please re-select your group.');
      } else {
        toast.error('Failed to save NR');
      }
    }
  };

  const currentTeeTime = round ? teeTimeForGroup(round, myGroupIdx) : null;
  const selectedGroupName = groupDisplayName(round, myGroupIdx);

  // scoringReady: skeleton stays until trip + round + resume-hole are all resolved
  const scoringReady = !loading && round && (resumeReady || !groupSelected || showGroupPicker);

  if (!scoringReady) return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="scoring-skeleton">
      <div className="h-14 bg-white animate-pulse border-b border-[#E1E6E4]" />
      <div className="px-4 pt-4 max-w-lg mx-auto space-y-3">
        <div className="h-10 w-40 bg-white rounded-lg animate-pulse border border-[#E1E6E4]" />
        <div className="h-24 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />
        <div className="h-24 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />
        <div className="h-24 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />
      </div>
    </div>
  );

  // Texas Scramble → delegate to dedicated component
  if (round?.scoring_mode === 'texas_scramble') return <ScrambleScoring />;

  if (!round) {
    // When session is expired and round never loaded, still render the blocking dialog
    // so the user isn't staring at a blank screen while the global redirect fires.
    if (sessionExpired) return (
      <AlertDialog open onOpenChange={() => {}}>
        <AlertDialogContent className="sm:max-w-sm rounded-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-[#1D242B]">Session Expired</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#455561]">
              Your scoring session has expired. Any scores entered since the last successful save have not been saved. Please log back in and re-enter them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => navigate(`/trip/${code}`)}
              className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-bold"
              data-testid="session-expired-relogin-btn">
              Log back in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
    return null;
  }

  // Other group indices (not the scorer's group)
  const otherGroupIndices = (round ? sortedGroupIndexes(round) : teams.map((_, i) => i)).filter(i => i !== myGroupIdx);

  return (
    <>
    {/* ── Session-expired blocking dialog ── */}
    <AlertDialog open={sessionExpired} onOpenChange={() => {}}>
      <AlertDialogContent className="sm:max-w-sm rounded-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-[#1D242B]">Session Expired</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            Your scoring session has expired. Any scores entered since the last successful save have not been saved. Please log back in and re-enter them.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={() => navigate(`/trip/${code}`)}
            className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-bold"
            data-testid="session-expired-relogin-btn">
            Log back in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <div className="min-h-screen bg-[#F4F6F5] pb-safe max-w-[100vw] overflow-x-hidden" data-testid="scoring-screen">
      {/* Header */}
      <div className="bg-[#1D242B] px-4 py-3">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1 flex-shrink-0" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg text-white font-bold truncate">{round.label}</h1>
            <p className="text-xs text-[#B8C7C0] truncate">{round.course?.name}{currentTeeTime ? ` · ${currentTeeTime}` : ''}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { sessionStorage.setItem('ft_scoring_hole', String(currentHole)); navigate(`/trip/${code}/round/${roundId}/leaderboard`); }}
            className="text-[#E59000] h-8 px-2 flex-shrink-0" data-testid="go-leaderboard-btn">
            <ChartBar className="w-4 h-4 mr-1" weight="fill" /> Board
          </Button>
          <SyncStatusPill
            unsavedCount={unsavedHoles.size + pendingOps.length}
            isSaving={savingKey !== null}
            lastSynced={lastSynced}
            pendingOps={pendingOps}
            onRetryAll={flushPending}
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1 ft-scroll">
          {Array.from({length: 18}, (_, i) => {
            const anyScored = players.some(p => (round.hole_strokes?.[p.id] || [])[i] > 0);
            return (
              <button key={i} onClick={() => navigateWithCheck(i)}
                className={`w-9 h-9 rounded-full flex-shrink-0 text-sm font-bold transition-all ${
                  i === currentHole ? 'bg-[#1B4332] text-white scale-110' :
                  anyScored ? 'bg-[#E8E5DB] text-[#1B4332]' : 'bg-[#2C3742] text-[#B8C7C0]'
                }`} data-testid={`hole-btn-${i}`}>{i+1}</button>
            );
          })}
        </div>
      </div>

      {/* Hole info bar */}
      <div className="bg-white border-b border-[#E1E6E4] px-4 py-3 flex items-center justify-between">
        <div>
          <span className="text-2xl font-bold text-[#1D242B]">Hole {currentHole+1}</span>
          <span className="text-base text-[#455561] ml-2">Par {par}</span>
        </div>
      </div>

      {/* iOS Home Screen enhanced install prompt */}
      <InstallPrompt variant="scoring" />

      {/* NR players in this group — undo option while round not revealed */}
      {!isRevealed && (round?.nr_players || []).some(id => myGroupPlayers.some(p => p.id === id)) && (
        <div className="px-4 py-2 bg-[#FFF8E1] border-b border-[#FFE082]" data-testid="nr-undo-banner">
          <p className="text-xs font-semibold text-[#E59000] mb-1.5">No Return (NR) players in this group:</p>
          {myGroupPlayers.filter(p => (round?.nr_players || []).includes(p.id)).map(p => (
            <div key={p.id} className="flex items-center justify-between py-1">
              <span className="text-sm text-[#455561]">{p.name}</span>
              <button onClick={() => handleUndoNr(p.id)} className="text-xs font-semibold text-[#007AFF] px-2 py-0.5 rounded border border-[#007AFF]/30 hover:bg-[#007AFF]/10 transition-colors" data-testid={`undo-nr-${p.id}`}>
                Undo NR
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Claim-moved banner — scoring moved to another phone */}
      {claimMoved && (
        <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[#FFF3E0] text-[#E59000] border-b border-[#FFE0B2]" data-testid="claim-moved-banner">
          <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
          <span className="flex-1">Scoring for {groupDisplayName(round, myGroupIdx)} has moved to another phone</span>
          <button onClick={() => {
            setTakeoverTarget({ group_idx: myGroupIdx, claimant_name: '', claimed_at: '', last_score_at: null, by_organiser: false });
          }}
            className="ml-auto px-3 py-1 text-xs font-bold bg-[#E59000] text-white rounded-lg hover:bg-[#D48400] transition-colors whitespace-nowrap"
            data-testid="take-it-back-btn">Take it back</button>
        </div>
      )}

      {/* Unsaved scores banner */}
      {pendingOps.length > 0 && !sessionExpired && !claimMoved && (
        <div className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-[#FFF8E1] text-[#F57F17] border-b border-[#FFE082]" data-testid="unsaved-scores-banner">
          <ArrowsClockwise className="w-4 h-4 flex-shrink-0 animate-spin" weight="bold" />
          <span>Scores not saved yet, retrying</span>
        </div>
      )}

      {/* Score lock banner (revealed OR group finished) */}
      {(isRevealed || isGroupFinished) && !claimMoved && (
        <div className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold ${
          scoreLocked
            ? 'bg-[#FFF3E0] text-[#E59000] border-b border-[#FFE0B2]'
            : 'bg-[#E8F5E9] text-[#2E7D32] border-b border-[#C8E6C9]'
        }`} data-testid="score-lock-banner">
          {scoreLocked ? (
            <>
              <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
              <span className="flex-1">{isGroupFinished && !isRevealed ? 'Group finished' : 'Round revealed'} — scores locked</span>
              <button
                onClick={() => setScoreUnlockDialogOpen(true)}
                className="ml-auto px-3 py-1 text-xs font-bold bg-[#E59000] text-white rounded-lg hover:bg-[#D48400] transition-colors whitespace-nowrap"
                data-testid="unlock-score-edit-btn"
              >
                Unlock (owner)
              </button>
            </>
          ) : (
            <>
              <LockOpen className="w-4 h-4 flex-shrink-0" weight="bold" />
              <span>Unlocked — edits will change the {isRevealed ? 'revealed leaderboard' : 'final scores'}</span>
            </>
          )}
        </div>
      )}
      <OwnerPasswordDialog
        open={scoreUnlockDialogOpen}
        onOpenChange={setScoreUnlockDialogOpen}
        onSuccess={(token) => { setScoreUnlockDialogOpen(false); setScoreEditUnlockToken(code, roundId, token); setScoreEditUnlocked(true); }}
        description="This round has been revealed. Enter the master password to unlock score editing."
        tripCode={code}
      />

      {/* Toggle + group bar */}
      {teams.length > 0 && (
        <div className="px-4 pt-3">
          {groupSelected && (
            <div className="flex items-center justify-between mb-2" data-testid="selected-group-bar">
              <div className="flex items-center gap-2">
                <UsersThree className="w-4 h-4 text-[#1B4332]" weight="fill" />
                <span className="text-sm font-semibold text-[#1D242B]">Scoring: {selectedGroupName}</span>
              </div>
              {isOrganiser && (
                <button onClick={() => setShowGroupPicker(true)}
                  className="text-xs text-[#1B4332] font-semibold flex items-center gap-1"
                  data-testid="change-group-btn">
                  <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Change
                </button>
              )}
            </div>
          )}
          <Tabs value={view} onValueChange={handleViewChange}>
            <TabsList className="w-full bg-[#F4F6F5] rounded-xl h-11">
              <TabsTrigger value="my-group" className="flex-1 rounded-lg text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#1D242B]" data-testid="tab-my-group">My Group</TabsTrigger>
              <TabsTrigger value="all" className="flex-1 rounded-lg text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#1D242B]" data-testid="tab-all-groups">All Groups</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* ── Scoring cards ── */}
      <div className="px-4 py-4 max-w-lg mx-auto space-y-3 pb-28">

        {/* Editable scorer's group (always shown) */}
        {(view === 'my-group' || (view === 'all' && groupSelected)) && (
          <>
            {view === 'all' && groupSelected && (
              <p className="text-xs font-semibold text-[#455561] uppercase tracking-wide" data-testid="your-group-label">Your group</p>
            )}
            {(() => {
              const teamScoringActive = trip && round && effectiveTeamScoring(trip, round) && roundFormat(round) !== 'nsp';
              const countingSet = teamScoringActive && round && teams[myGroupIdx]
                ? countingPlayersForHole(round, teams[myGroupIdx], currentHole, par)
                : new Set();
              return myGroupPlayers.map(player => {
                const strokes = getStrokes(player.id);
                const ph = getPH(player);
                const playerPar = getPlayerPar(player.id);
                const playerSI = getPlayerSI(player.id);
                const shots = shotsOnHole(ph, playerSI);
                const holeIsNr = isHoleNr(player.id, currentHole);
                const stab = holeIsNr ? null : (strokes > 0 ? stablefordPoints(strokes, playerPar, ph, playerSI) : null);
                const colorClass = strokes > 0 ? scoreColorClass(strokes, playerPar) : '';
                const teeName = getTeeName(player.id);
                return (
                  <EditablePlayerCard key={player.id} player={player} strokes={strokes} ph={ph} shots={shots}
                    stab={stab} colorClass={colorClass} teeName={teeName} counts={countingSet.has(player.id)}
                    isSaving={savingKey === `${player.id}-${currentHole}`}
                    isUnsaved={unsavedHoles.has(`${player.id}-${currentHole}`)}
                    onMinus={() => handleStroke(player, -1)} onPlus={() => handleStroke(player, 1)} onDirectInput={handleDirectInput} fmt={roundFormat(round)} holePar={playerPar} defaultPar={par} playerSI={playerSI} disabled={scoreLocked}
                    isHoleNr={isHoleNr(player.id, currentHole)} onToggleNr={() => handleToggleHoleNr(player)} />
                );
              });
            })()}
          </>
        )}

        {/* "All Groups" mode without group selected: show all as read-only */}
        {view === 'all' && !groupSelected && teams.length > 0 && (
          <>
            <p className="text-xs text-[#455561] text-center py-1">Select a group above to enable editing</p>
            {(round ? sortedGroupIndexes(round) : teams.map((_, i) => i)).map(idx => (
              <ReadOnlyGroupCard key={idx} groupIdx={idx}
                groupName={groupDisplayName(round, idx)}
                teamPlayerIds={teams[idx]} players={players} round={round}
                currentHole={currentHole} par={par} si={si}
                expanded={expandedGroups.has(idx)}
                onToggle={() => toggleGroupExpand(idx)}
                teamScoringActive={trip && round && effectiveTeamScoring(trip, round) && roundFormat(round) !== 'nsp'}
                hideScores={hideOtherScores} />
            ))}
          </>
        )}

        {/* "All Groups" mode with group selected: other groups as collapsed read-only */}
        {view === 'all' && groupSelected && otherGroupIndices.length > 0 && (
          <>
            <p className="text-xs font-semibold text-[#455561] uppercase tracking-wide mt-4" data-testid="other-groups-label">Other groups</p>
            {otherGroupIndices.map(idx => (
              <ReadOnlyGroupCard key={idx} groupIdx={idx}
                groupName={groupDisplayName(round, idx)}
                teamPlayerIds={teams[idx]} players={players} round={round}
                currentHole={currentHole} par={par} si={si}
                expanded={expandedGroups.has(idx)}
                onToggle={() => toggleGroupExpand(idx)}
                teamScoringActive={trip && round && effectiveTeamScoring(trip, round) && roundFormat(round) !== 'nsp'}
                hideScores={hideOtherScores} />
            ))}
          </>
        )}

        {/* No teams — flat list of all active players, editable */}
        {teams.length === 0 && players.filter(p => p.active !== false).map(player => {
          const strokes = getStrokes(player.id);
          const ph = getPH(player);
          const playerPar = getPlayerPar(player.id);
          const playerSI = getPlayerSI(player.id);
          const shots = shotsOnHole(ph, playerSI);
          const holeIsNr = isHoleNr(player.id, currentHole);
          const stab = holeIsNr ? null : (strokes > 0 ? stablefordPoints(strokes, playerPar, ph, playerSI) : null);
          const colorClass = strokes > 0 ? scoreColorClass(strokes, playerPar) : '';
          const teeName = getTeeName(player.id);
          return (
            <EditablePlayerCard key={player.id} player={player} strokes={strokes} ph={ph} shots={shots}
              stab={stab} colorClass={colorClass} teeName={teeName}
              isSaving={savingKey === `${player.id}-${currentHole}`}
              isUnsaved={unsavedHoles.has(`${player.id}-${currentHole}`)}
              onMinus={() => handleStroke(player, -1)} onPlus={() => handleStroke(player, 1)} onDirectInput={handleDirectInput} fmt={roundFormat(round)} holePar={playerPar} defaultPar={par} playerSI={playerSI} disabled={scoreLocked} />
          );
        })}
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E1E6E4] px-4 py-3 flex items-center gap-3 z-20">
        <Button onClick={handlePrev} disabled={currentPosInOrder === 0} variant="outline"
          className="h-14 px-4 rounded-xl border-[#E1E6E4]" data-testid="prev-hole-btn">
          <CaretLeft className="w-5 h-5" weight="bold" />
        </Button>
        <Button onClick={handleNext}
          className={`flex-1 h-14 rounded-xl text-base font-bold ${
            currentPosInOrder === 17 ? 'bg-[#E59000] hover:bg-[#CC8000]' : 'bg-[#1B4332] hover:bg-[#163B2B]'
          } text-white`} data-testid="next-hole-btn">
          {currentPosInOrder === 17 ? (isGroupFinished ? 'Back to Round' : 'Finish Round') : `Next — Hole ${playingOrder[currentPosInOrder + 1] + 1}`}
        </Button>
      </div>

      {/* Group picker modal */}
      <Dialog open={showGroupPicker} onOpenChange={setShowGroupPicker}>
        <DialogContent className="sm:max-w-md mx-auto" data-testid="group-picker-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1D242B]">Which group are you scoring?</DialogTitle>
            <DialogDescription className="text-sm text-[#455561]">Select your group to filter the scoring view.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
            {(round ? sortedGroupIndexes(round) : teams.map((_, i) => i)).map(idx => {
              const teamPlayerIds = teams[idx] || [];
              const groupName = groupDisplayName(round, idx);
              const teeTime = teeTimeForGroup(round, idx);
              const sh = startHoleForGroup(round, idx);
              const names = teamPlayerIds.map(id => players.find(p => p.id === id)?.name).filter(Boolean).join(', ');
              const claim = (round.group_claims || []).find(c => c.group_idx === idx);
              const claimActive = claim && new Date(claim.expires_at) > new Date();
              const myClaimData = getGroupClaimData(code, roundId);
              const isMyGroup = myClaimData?.group_idx === idx && myClaimData?.token;
              const claimedByOther = claimActive && !isMyGroup;

              // Only finished groups in non-revealed rounds are truly disabled
              const gf = round?.group_finished || [];
              const groupIsFinished = idx < gf.length && gf[idx];
              const roundIsRevealed = !!round?.revealed;
              const hardLocked = groupIsFinished || roundIsRevealed;

              // Organiser release: check if any scores exist for this group
              const hasScoresInGroup = claimActive && isOrganiser && teamPlayerIds.some(pid => {
                const strokes = round?.hole_strokes?.[pid] || [];
                const nr = round?.hole_nr?.[pid] || [];
                return nr.length > 0 || strokes.some(s => s && s > 0);
              });

              // Time-ago helper for claim display
              const timeAgo = (isoStr) => {
                if (!isoStr) return null;
                const ago = Math.round((Date.now() - new Date(isoStr).getTime()) / 60000);
                return ago < 1 ? 'just now' : ago < 60 ? `${ago}m ago` : `${Math.round(ago / 60)}h ago`;
              };

              const handleRelease = async (e) => {
                e.stopPropagation();
                try {
                  const res = await releaseGroup(code, roundId, { group_idx: idx });
                  setRound(res.data.rounds.find(r => r.id === roundId));
                  if (isMyGroup) clearGroupClaim(code, roundId);
                  toast.success(`${groupName} released`);
                } catch (err) {
                  if (err?.response?.data?.detail === 'scores_exist') {
                    toast.error("Can't release — scores already entered");
                  } else {
                    toast.error('Failed to release group');
                  }
                }
              };

              return (
                <button key={idx} onClick={() => !hardLocked && setConfirmGroupIdx(idx)}
                  disabled={hardLocked}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    hardLocked ? 'border-[#E1E6E4] bg-[#F9F9F9] opacity-50 cursor-not-allowed'
                    : isMyGroup ? 'border-[#1B4332] bg-[#E8E5DB]'
                    : myGroupIdx === idx && groupSelected ? 'border-[#1B4332] bg-[#E8E5DB]'
                    : 'border-[#E1E6E4] bg-white hover:border-[#1B4332]/50'
                  }`} data-testid={`pick-group-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-bold ${hardLocked ? 'text-[#B8C7C0]' : 'text-[#1D242B]'}`}>{groupName}</span>
                    <div className="flex items-center gap-2">
                      {isMyGroup && <Badge className="bg-[#1B4332] text-white text-[10px]">Your group</Badge>}
                      {hardLocked && groupIsFinished && <Badge className="bg-[#B8C7C0] text-white text-[10px]">Finished</Badge>}
                      {hardLocked && roundIsRevealed && !groupIsFinished && <Badge className="bg-[#B8C7C0] text-white text-[10px]">Revealed</Badge>}
                      <span className="text-xs text-[#455561]">{teamPlayerIds.length} players</span>
                      {teeTime && <Badge className="bg-[#1D242B] text-white text-[10px] font-mono px-1.5">{teeTime}</Badge>}
                      {!(round?.start_type === 'standard' && sh === (round?.starting_hole || 1)) && (
                        <Badge className="bg-[#E59000] text-white text-[10px] px-1.5">Hole {holeLabelForGroup(round, idx).slice(1)}</Badge>
                      )}
                    </div>
                  </div>
                  {names && <p className="text-xs text-[#455561] mt-1">{names}</p>}
                  {/* Claimed-by-other note: selectable, routes to takeover */}
                  {claimedByOther && !hardLocked && (
                    <p className="text-[10px] text-[#E59000] mt-1.5 font-medium" data-testid={`claim-note-${idx}`}>
                      Being scored on another phone{claim.by_organiser ? ' (by the organiser)' : ''}
                      {claim.last_score_at ? ` · last score ${timeAgo(claim.last_score_at)}` : ` · selected ${timeAgo(claim.claimed_at)}`}
                    </p>
                  )}
                  {/* Organiser release control */}
                  {isOrganiser && claimActive && !hardLocked && (
                    <div className="mt-2 flex items-center justify-end">
                      {hasScoresInGroup ? (
                        <span className="text-[10px] text-[#B8C7C0] italic" data-testid={`release-blocked-${idx}`}>Can't release — scores entered</span>
                      ) : (
                        <span onClick={handleRelease}
                          className="text-[10px] font-semibold text-[#D32F2F] hover:text-[#B71C1C] cursor-pointer px-2 py-1 rounded-md hover:bg-[#FFF3F3] transition-colors"
                          data-testid={`release-group-${idx}`}>Release</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <button onClick={() => { setView('all'); setShowGroupPicker(false); }}
            className="w-full text-center text-sm text-[#455561] py-2 mt-1 hover:text-[#1D242B]"
            data-testid="skip-group-picker">Skip — show all groups</button>
        </DialogContent>
      </Dialog>

      {/* Group confirmation modal */}
      <Dialog open={confirmGroupIdx !== null} onOpenChange={(open) => { if (!open) setConfirmGroupIdx(null); }}>
        <DialogContent className="sm:max-w-sm mx-auto" data-testid="group-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1D242B]">Confirm your group</DialogTitle>
            <DialogDescription className="text-sm text-[#455561]">
              Make sure these are the players in your group.
            </DialogDescription>
          </DialogHeader>
          {confirmGroupIdx !== null && (() => {
            const gIdx = confirmGroupIdx;
            const gName = groupDisplayName(round, gIdx);
            const gTeeTime = round ? teeTimeForGroup(round, gIdx) : null;
            const gStartHole = round ? startHoleForGroup(round, gIdx) : 1;
            const gPlayerIds = teams[gIdx] || [];
            const gPlayers = gPlayerIds.map(id => players.find(p => p.id === id)).filter(Boolean);
            return (
              <div className="mt-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base font-bold text-[#1D242B]">{gName}</span>
                  {gTeeTime && <Badge className="bg-[#1D242B] text-white text-[10px] font-mono px-1.5">{gTeeTime}</Badge>}
                  {gStartHole > 1 && <Badge className="bg-[#E59000] text-white text-[10px] px-1.5">Hole {gStartHole}</Badge>}
                </div>
                <div className="space-y-2 mb-4">
                  {gPlayers.map(p => (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 bg-[#F4F6F5] rounded-lg" data-testid={`confirm-player-${p.id}`}>
                      <div className="w-8 h-8 rounded-full bg-[#E8E5DB] flex items-center justify-center text-[#1B4332] font-bold text-xs flex-shrink-0">
                        {p.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-[#1D242B]">{p.name}</span>
                      <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-1.5 ml-auto flex-shrink-0">
                        WHS {displayWHS(p)}
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1 h-12 rounded-xl border-[#E1E6E4] text-[#455561] font-semibold"
                    onClick={() => setConfirmGroupIdx(null)} data-testid="confirm-group-cancel">
                    Cancel
                  </Button>
                  <Button className="flex-1 h-12 rounded-xl bg-[#1B4332] hover:bg-[#163B2B] text-white font-semibold"
                    onClick={() => { const idx = confirmGroupIdx; setConfirmGroupIdx(null); selectGroup(idx); }}
                    data-testid="confirm-group-submit">
                    Confirm
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>



    {/* All-Par confirmation dialog */}
    <AlertDialog open={allParConfirmOpen} onOpenChange={setAllParConfirmOpen}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="all-par-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">No Scores Entered</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            No scores were entered for <span className="font-semibold text-[#1D242B]">Hole {currentHole + 1}</span>. Confirm all players made Par?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="all-par-cancel-btn">Go back</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white"
            data-testid="all-par-confirm-btn"
            onClick={handleAllParConfirm}>
            Confirm — all Par
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Stage 1: Soft prompt — one missing hole at a time */}
    <AlertDialog open={softPromptItem != null}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="soft-prompt-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Missing Score</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            <span className="font-semibold text-[#1D242B]">Hole {softPromptItem != null ? softPromptItem + 1 : ''}</span> needs a score.
            {softPromptQueue.length > 1 && (
              <span className="block mt-1 text-xs text-[#B8C7C0]">{softPromptQueue.length - 1} more missing {softPromptQueue.length - 1 === 1 ? 'hole' : 'holes'} after this</span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <Button
            variant="outline"
            className="text-[#455561]"
            data-testid="soft-prompt-skip-btn"
            onClick={handleSoftPromptSecondary}>
            {softPromptIsFinish ? 'Skip — mark as NR' : 'Remind me later'}
          </Button>
          <Button
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white"
            data-testid="soft-prompt-goto-btn"
            onClick={handleSoftPromptGoTo}>
            Go to Hole {softPromptItem != null ? softPromptItem + 1 : ''}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={!!finishNrDialog} onOpenChange={(open) => { if (!open) setFinishNrDialog(null); }}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="nr-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Incomplete Rounds Detected</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-[#455561] space-y-3">
              <p>The following players have incomplete rounds and will be marked <strong>No Return (NR)</strong>:</p>
              <ul className="space-y-1.5">
                {finishNrDialog?.nrPlayers?.map(p => (
                  <li key={p.id} className="flex items-start gap-2">
                    <span className="font-semibold text-[#1D242B]">{p.name}</span>
                    <span className="text-[#455561]">— Hole{p.missingHoles.length > 1 ? 's' : ''} {p.missingHoles.join(', ')} missing</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#B8C7C0]">They will not be ranked or counted for medals/countback. Players with complete rounds will still be ranked normally.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="nr-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white"
            data-testid="nr-confirm-btn"
            onClick={() => { setFinishNrDialog(null); setFinishConfirmOpen(true); }}>
            Confirm NR and Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Final Finish Confirmation Dialog */}
    <AlertDialog open={finishConfirmOpen} onOpenChange={setFinishConfirmOpen}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="finish-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Finish Round</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            Please confirm you have finished your round — no changes can be made after.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="finish-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white"
            data-testid="finish-confirm-btn"
            onClick={() => { setFinishConfirmOpen(false); doFinishRound(pendingNrPlayerIds); }}>
            Confirm and Finish Round
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Takeover modal */}
    <AlertDialog open={takeoverTarget !== null} onOpenChange={(open) => { if (!open) setTakeoverTarget(null); }}>
      <AlertDialogContent className="max-w-sm rounded-xl" data-testid="takeover-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[#1D242B]">Take over scoring?</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-[#455561]">
            {takeoverTarget?.last_score_at ? (
              <>Someone else is scoring {groupDisplayName(round, takeoverTarget?.group_idx ?? 0)}{takeoverTarget?.by_organiser ? ' (by the organiser)' : ''} (last score entered {(() => {
                const ago = Math.round((Date.now() - new Date(takeoverTarget.last_score_at).getTime()) / 60000);
                return ago < 1 ? 'just now' : ago < 60 ? `${ago}m ago` : `${Math.round(ago / 60)}h ago`;
              })()}). Take over scoring on this phone?</>
            ) : (
              <>{groupDisplayName(round, takeoverTarget?.group_idx ?? 0)} was selected on another phone{takeoverTarget?.by_organiser ? ' (by the organiser)' : ''}{takeoverTarget?.claimed_at ? ` ${(() => {
                const ago = Math.round((Date.now() - new Date(takeoverTarget.claimed_at).getTime()) / 60000);
                return ago < 1 ? 'just now' : ago < 60 ? `${ago}m ago` : `${Math.round(ago / 60)}h ago`;
              })()}` : ''}. Take over scoring on this phone?</>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="takeover-cancel-btn" disabled={takeoverLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[#1B4332] hover:bg-[#163B2B] text-white"
            data-testid="takeover-confirm-btn"
            disabled={takeoverLoading}
            onClick={handleTakeover}>
            {takeoverLoading ? 'Taking over…' : 'Take over'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
}
